import styled, { keyframes } from 'styled-components';
import { theme } from '../../../styles/theme';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeLeft = keyframes`
  from { opacity: 0; transform: translateX(36px); }
  to { opacity: 1; transform: translateX(0); }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

const breathe = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 1; }
`;

const loadbar = keyframes`
  to { width: 84%; }
`;

const bounce = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

export const Container = styled.div`
  min-height: 100vh;
  padding: 68px 8% 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 3rem;
  background: ${theme.colors.white};
  position: relative;
  overflow: hidden;

  @media (max-width: 960px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 88px 5% 4rem;
  }

  @media (max-width: 600px) {
    align-items: center;
    text-align: center;
  }
`;

export const Blob1 = styled.div`
  position: absolute;
  width: 650px;
  height: 650px;
  background: radial-gradient(
    circle,
    rgba(253, 131, 96, 0.1) 0%,
    transparent 68%
  );
  top: -120px;
  right: -80px;
  border-radius: 50%;
  pointer-events: none;
`;

export const Blob2 = styled.div`
  position: absolute;
  width: 380px;
  height: 380px;
  background: radial-gradient(
    circle,
    rgba(0, 102, 255, 0.05) 0%,
    transparent 70%
  );
  bottom: -40px;
  left: 5%;
  border-radius: 50%;
  pointer-events: none;
`;

export const Content = styled.div`
  flex: 1;
  max-width: 560px;
  position: relative;
  z-index: 2;
  padding: 4rem 0;

  @media (max-width: 600px) {
    text-align: center;
  }
`;

export const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${theme.colors.orangeLight};
  color: ${theme.colors.orangeDark};
  border: 1px solid rgba(253, 131, 96, 0.25);
  padding: 0.4rem 1rem;
  border-radius: 100px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 2rem;
  opacity: 0;
  animation: ${fadeUp} 0.6s 0.1s ease forwards;
`;

export const BadgeDot = styled.span`
  width: 7px;
  height: 7px;
  background: ${theme.colors.orange};
  border-radius: 50%;
  animation: ${blink} 2s infinite;
`;

export const Title = styled.h1`
  font-family: ${theme.fonts.display};
  font-size: clamp(2.8rem, 5.5vw, 5rem);
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.04em;
  color: ${theme.colors.black};
  margin-bottom: 1.5rem;
  opacity: 0;
  animation: ${fadeUp} 0.6s 0.2s ease forwards;

  em {
    font-style: normal;
    color: ${theme.colors.orange};
  }
`;

export const Subtitle = styled.p`
  font-size: 1.05rem;
  color: ${theme.colors.mid};
  line-height: 1.72;
  font-weight: 400;
  max-width: 440px;
  margin-bottom: 2.5rem;
  opacity: 0;
  animation: ${fadeUp} 0.6s 0.3s ease forwards;

  @media (max-width: 600px) {
    margin: 0 auto 2.5rem;
  }
`;

export const CTAs = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  opacity: 0;
  animation: ${fadeUp} 0.6s 0.4s ease forwards;

  @media (max-width: 600px) {
    justify-content: center;
  }
`;

export const BtnPrimary = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  background: ${theme.colors.orange};
  color: white;
  padding: 0.9rem 2rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 4px 20px rgba(253, 131, 96, 0.35);

  &:hover {
    background: ${theme.colors.orangeDark};
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(253, 131, 96, 0.45);
  }
`;

export const BtnSecondary = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${theme.colors.dark};
  padding: 0.9rem 1.5rem;
  border-radius: 10px;
  font-weight: 500;
  font-size: 0.95rem;
  text-decoration: none;
  border: 1.5px solid ${theme.colors.border};
  transition: all 0.2s;

  &:hover {
    border-color: ${theme.colors.black};
    color: ${theme.colors.black};
  }
`;

export const SocialProof = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  opacity: 0;
  animation: ${fadeUp} 0.6s 0.5s ease forwards;

  @media (max-width: 600px) {
    justify-content: center;
  }
`;

export const Avatars = styled.div`
  display: flex;

  span {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 2.5px solid ${theme.colors.white};
    background: linear-gradient(135deg, ${theme.colors.orange}, #ffa07a);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
    font-weight: 700;
    color: white;
    margin-left: -9px;

    &:first-child {
      margin-left: 0;
    }
  }
`;

export const ProofText = styled.div`
  font-size: 0.83rem;
  color: ${theme.colors.mid};
  line-height: 1.45;

  strong {
    color: ${theme.colors.black};
    font-weight: 600;
  }
`;

/* Hero Visual - Phone Mockup */
export const HeroVisual = styled.div`
  flex: 0 0 auto;
  width: 100%;
  max-width: 420px;
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem 1rem 4rem 0;
  opacity: 0;
  animation: ${fadeLeft} 0.8s 0.3s ease forwards;

  @media (max-width: 960px) {
    width: 100%;
    max-width: 100%;
    justify-content: center;
    padding: 0 0 2rem;
  }
`;

export const MockupContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  img {
    width: 100%;
    max-width: 500px;
    height: auto;
    animation: ${bounce} 3s ease-in-out infinite;
  }
`;

export const PhoneScene = styled.div`
  position: relative;
  width: 100%;
  max-width: 420px;

  @media (max-width: 960px) {
    max-width: 360px;
  }
`;

export const PhoneGlow = styled.div`
  position: absolute;
  inset: -60px;
  background: radial-gradient(
    ellipse at center,
    rgba(253, 131, 96, 0.15) 0%,
    transparent 65%
  );
  border-radius: 50%;
  animation: ${breathe} 4s ease-in-out infinite;
`;

export const PhoneFrame = styled.div`
  position: relative;
  width: 100%;

  img {
    width: 100%;
    height: auto;
    display: block;
    animation: ${bounce} 3s ease-in-out infinite;
  }
`;

export const PhoneNotch = styled.div`
  width: 80px;
  height: 24px;
  background: #111;
  border-radius: 0 0 16px 16px;
  margin: 0 auto 8px;
`;

export const PhoneScreen = styled.div`
  background: linear-gradient(180deg, #0066ff 0%, #0052cc 100%);
  border-radius: 30px;
  overflow: hidden;
  min-height: 580px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const AppLoginWrap = styled.div`
  padding: 1.5rem 1.2rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

export const AppLoginLogo = styled.div`
  margin-bottom: 0.5rem;

  img {
    height: 36px;
    width: auto;
    filter: brightness(0) invert(1);
  }
`;

export const AppLoginTagline = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.75rem;
  text-align: center;
  line-height: 1.4;
  margin-bottom: 0.5rem;
  max-width: 200px;
`;

export const AppLoginInput = styled.input`
  width: 100%;
  padding: 0.7rem 1rem;
  border-radius: 10px;
  border: none;
  font-size: 0.8rem;
  background: rgba(255, 255, 255, 0.95);
  color: ${theme.colors.black};

  &::placeholder {
    color: ${theme.colors.mid};
  }
`;

export const AppLoginBtn = styled.button`
  width: 100%;
  padding: 0.75rem;
  background: ${theme.colors.orange};
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
`;

export const AppLoginLink = styled.span`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.9);
  margin-top: 0.5rem;

  strong {
    text-decoration: underline;
    font-weight: 600;
    cursor: pointer;
  }
`;

export const AppTopbar = styled.div`
  background: ${theme.colors.orange};
  padding: 1.1rem 1.1rem 0.8rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const AppLogoText = styled.div`
  font-family: ${theme.fonts.display};
  font-weight: 800;
  font-size: 1.05rem;
  color: white;
  letter-spacing: -0.02em;
`;

export const AppBell = styled.div`
  width: 26px;
  height: 26px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
`;

export const AppBody = styled.div`
  background: #f5f5f5;
  padding: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

export const AppSectionTitle = styled.div`
  font-size: 0.65rem;
  font-weight: 700;
  color: ${theme.colors.mid};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

export const AppCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 0.9rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
`;

export const AppCardHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.35rem;
`;

export const AppCardTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  color: ${theme.colors.black};
`;

export const AppCardBadge = styled.div`
  font-size: 0.6rem;
  font-weight: 700;
  background: ${theme.colors.orangeLight};
  color: ${theme.colors.orangeDark};
  padding: 0.12rem 0.45rem;
  border-radius: 100px;
`;

export const AppCardSub = styled.div`
  font-size: 0.67rem;
  color: ${theme.colors.mid};
  margin-bottom: 0.55rem;
`;

export const ProgressTrack = styled.div`
  height: 5px;
  background: ${theme.colors.bgLight};
  border-radius: 100px;
  overflow: hidden;
`;

export const ProgressFill = styled.div`
  height: 100%;
  background: ${theme.colors.orange};
  border-radius: 100px;
  width: 0;
  animation: ${loadbar} 1.6s 0.5s ease-out forwards;
`;

export const AppStatsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.5rem;
`;

export const AppMiniStat = styled.div`
  background: white;
  border-radius: 10px;
  padding: 0.55rem;
  text-align: center;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);

  .n {
    font-family: ${theme.fonts.display};
    font-size: 0.92rem;
    font-weight: 800;
    color: ${theme.colors.black};
  }
  .l {
    font-size: 0.56rem;
    color: ${theme.colors.mid};
  }
`;

export const AppCtaBtn = styled.button`
  background: ${theme.colors.orange};
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.7rem;
  font-weight: 700;
  font-family: ${theme.fonts.display};
  font-size: 0.78rem;
  width: 100%;
  cursor: pointer;
  box-shadow: 0 3px 12px rgba(253, 131, 96, 0.35);
`;

const floatA = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const floatB = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

export const FloatNotif = styled.div<{ $position: 'a' | 'b' }>`
  position: absolute;
  background: white;
  border-radius: 14px;
  padding: 0.7rem 0.9rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  border: 1px solid ${theme.colors.border};
  display: flex;
  align-items: center;
  gap: 0.55rem;
  white-space: nowrap;
  font-size: 0.75rem;
  right: ${(p) => (p.$position === 'a' ? '-20px' : 'auto')};
  left: ${(p) => (p.$position === 'b' ? '-20px' : 'auto')};
  top: ${(p) => (p.$position === 'a' ? '18%' : 'auto')};
  bottom: ${(p) => (p.$position === 'b' ? '22%' : 'auto')};
  animation: ${(p) => (p.$position === 'a' ? floatA : floatB)}
    ${(p) => (p.$position === 'a' ? '3.5s' : '4s')} ease-in-out infinite;

  @media (max-width: 960px) {
    display: none;
  }
`;

export const FloatNotifIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: ${theme.colors.orangeLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  flex-shrink: 0;
`;

export const FloatNotifText = styled.div`
  strong {
    display: block;
    font-weight: 600;
    color: ${theme.colors.black};
    font-size: 0.75rem;
  }
  span {
    color: ${theme.colors.mid};
    font-size: 0.67rem;
  }
`;
